import { Database } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";

const MEASURED = [
  ["Roads, bridges, ports, shelters, parishes, buildings", "OpenStreetMap (Overpass / openstreetmap.fr extract)"],
  ["Ground elevation of every asset + flood raster", "Bare-earth DeltaDTM v1.1 (TU Delft/Deltares 2024, CC-BY 4.0) below 30 m; SRTM above; LiDAR overrides both when loaded; ~37 m grid"],
  ["Coastline (land vs sea)", "OpenStreetMap natural=coastline"],
  ["Live sea level", "IOC tide gauge Bridgetown (brid2), 1-min"],
  ["Tide prediction + high-tide line (MHHW)", "11-constituent harmonic analysis of 6 months of brid2 data"],
  ["Live waves / wind / pressure / rain + 72 h forecast", "Open-Meteo, NOAA NDBC buoy 41040"],
  ["Wave forecast + past-year normal sea state (height, peak period, direction)", "ECMWF WAM 0.25° via Open-Meteo marine, 4 offshore points (N/E/S/W)"],
  ["Satellite flood check (Elsa, Beryl, latest pass)", "Sentinel-1 radar, Copernicus Data Space Ecosystem (~19 m)"],
  ["Before/after colour photos", "Sentinel-2 L2A true colour, least cloudy day by scene classification (cloud % shown)"],
  ["Active storms, official cones, wind-arrival times & probabilities", "NOAA National Hurricane Center GIS"],
  ["Historical storm tracks, intensity, 34-kt wind radii", "NOAA HURDAT2 best track"],
  ["Historical weather at Barbados", "ERA5 reanalysis — weather via Open-Meteo archive; wave height, peak period and direction direct from Copernicus Climate Data Store (nearest 0.5° wave cell, 16 km)"],
  ["Parish population", "Barbados Statistical Service, 2010 Census"],
];
const PHYSICS = [
  "Flooding: ground below still-water level AND connected to the sea (connected-component analysis)",
  "Still-water level: measured gauge (live) or harmonic tide + inverse barometer 1 cm/hPa (forecast/replay)",
  "Wave run-up R2% (Stockdon et al. 2006) per asset from the bare-earth terrain foreshore slope + coast orientation; oblique waves EurOtop (2018); lee coasts get none",
  "Run-up reaches an asset only if it and all ground between it and the sea are below tide + pressure + run-up; land reached in normal seas (99th percentile of the past year) counts as beach",
  "Radar flooding: Sentinel-1 VV change detection (UN-SPIDER) — after < −18 dB and ≥ 3 dB drop vs same-orbit 'before' pass, slopes < 5 % only",
  "Storm wind/pressure profile: Holland (1980), Rmax Willoughby & Rahn (2004), B Vickery & Wadhera (2008)",
  "Gully runoff: Rational Method Q = C·i·A over D8 catchments from the DEM",
  "Isolation: network graph of roads/bridges to live ports",
  "Tiers & threat: computed outcomes only (flooded now / in 72 h forecast / in a recorded storm)",
];
const ESTIMATES = [
  "Shelter capacity: building floor area × 0.6 ÷ 3.5 m²/person (Sphere standard) — official DEM figures not public",
  "Share of displaced residents going to a shelter: 25% — no Barbados evacuation data available",
  "Gully bank-full capacity curve and runoff coefficients — no surveyed channel dimensions",
  "DeltaDTM vertical error ~0.4 m (EGM2008 datum, not tied to the Bridgetown gauge); above 30 m heights are SRTM (roof/tree tops)",
  "Each coast uses the wave-model cell offshore of it; waves arriving side-on or from behind a coast give no run-up (no nearshore refraction/diffraction or reef-breaking model)",
  "Radar passes come 1–3 days after a storm; short-lived flooding may have drained before the satellite looked",
  "Drills (C1–C5) are synthetic training storms (surge, wind, rain and storm size), labelled SIMULATED — the tide under them is the real Bridgetown prediction",
  "Surge slider is a manual what-if input, not a measurement",
];

const Section = ({ title, color, children }) => (
  <div>
    <div className="font-mono-t text-[10px] uppercase tracking-widest mb-1.5" style={{ color }}>{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

export default function DataSourcesDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="data-sources-button" className="glass flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border font-mono-t text-[10px] uppercase tracking-wide text-slate-200 hover:border-cyan/60 hover:text-cyan transition-colors">
          <Database className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Data &amp; assumptions</span>
        </button>
      </DialogTrigger>
      <DialogContent data-testid="data-sources-dialog" className="bg-[#06163d] border-border text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide">Data &amp; assumptions</DialogTitle>
          <DialogDescription className="font-mono-t text-[11px] text-slate-400">Everything on screen, and where it comes from.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 font-mono-t text-[11px] text-slate-300">
          <Section title="Measured / official data" color="#30D158">
            {MEASURED.map(([what, src]) => (
              <div key={what} className="flex gap-2"><span className="text-slate-100 w-1/2">{what}</span><span className="text-slate-400 w-1/2">{src}</span></div>
            ))}
          </Section>
          <Section title="Published physics & methods" color="#FCC109">
            {PHYSICS.map((t) => <div key={t}>· {t}</div>)}
          </Section>
          <Section title="Estimates still in use (labelled)" color="#FF9F0A">
            {ESTIMATES.map((t) => <div key={t} data-testid="data-estimate-item">· {t}</div>)}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
